<template>
  <header
    class="wc-mheader__wrapper container-fluid"
    role="banner"
    :class="scrollClass"
    id="mobile-header-v2"
  >
    <section>
      <div><WCActiveOrderTypeBadgeModern /></div>
    </section>
    <section class="wc-mheader__middle py-2 container-fluid">
      <div class="row align-items-center marginMenu">
        <WCHamburgerMenu @showMenu="toggleSideMenu" />
        <div class="col-6 my-auto" v-if="isNotAnEmployee">
          <router-link class="float-left" to="/" v-if="!isCustomerModeScan">
            <WCImage
              :imageSrc="brandImageSrc"
              :imageAlt="getStoreName"
              :imageClass="brandImageClass"
              :defaultClass="brandImageClass"
              :defaultTitle="getStoreName"
              data-testid="mobile-header-v2-brand"
              @imgLoadError="fallBackToStoreLogo"
            />
          </router-link>
          <div v-else>
            <WCImage
              :imageSrc="brandImageSrc"
              :imageAlt="getStoreName"
              :imageClass="brandImageClass"
              :defaultClass="brandImageClass"
              :defaultTitle="getStoreName"
              @imgLoadError="fallBackToStoreLogo"
            />
          </div>
        </div>
        <div class="col-6 my-auto wc-mobile-header-v2__brand-container--employee" v-else>
          <WCImage
            :imageSrc="brandImageSrc"
            :imageAlt="getStoreName"
            :imageClass="brandImageClass"
            :defaultClass="brandImageClass"
            :defaultTitle="getStoreName"
            @imgLoadError="fallBackToStoreLogo"
          />
        </div>
        <div class="col row header-options justify-content-end" v-if="isNotAnEmployee">
          <WCLoginMenuV2 v-if="isGuest" />
          <button
            v-else
            class="unstyled-btn wc-mobile-header-v2__profile-button"
            :class="{
              'wc-mobile-header-v2__profile-button--customer-assoc': isEmployee && isCustomer,
            }"
            type="button"
            :aria-label="$t('accountSettings')"
            data-testid="mobile-header-v2-profile"
            @click="goToAccountSettings"
          >
            <WCSvgIcon name="user" class="wc-mobile-header-v2__profile-icon" />
            <WCUserGreetingV2
              :layout="userGreetingLayout.HEADER"
              :showCustomerContext="$route.name !== 'EmployeeCustomerSelect'"
            />
          </button>
        </div>
      </div>
    </section>
    <section v-if="isNotAnEmployee" class="container-fluid">
      <div class="d-flex align-items-baseline pb-2">
        <button
          class="unstyled-btn wc-mobile-header-v2__store-pill"
          type="button"
          data-testid="mobile-header-v2-store-pill"
          :aria-label="$t('selectStore')"
          @click="goToSelectStore"
        >
          <WCSvgIcon name="location-marker" class="wc-mobile-header-v2__store-icon" />
          <span class="wc-mobile-header-v2__store-label">
            {{ getStoreName }}
          </span>
          <WCSvgIcon name="chevron-down" class="wc-mobile-header-v2__chevron" />
        </button>
      </div>
    </section>
  </header>
</template>

<script>
import { markRaw } from 'vue';
import UserMixin from '@/modules/user/mixins/UserMixin';
import SideMenuService from '@/modules/sidebar/services/SideMenuService';
import WCHamburgerMenu from '@/components/WCHamburgerMenu/WCHamburgerMenu.vue';
import HeaderScrollTransitionMixin from '@/modules/platform/mixins/HeaderScrollTransitionMixin';
import EmployeeMixin from '@/modules/employee/mixins/EmployeeMixin';
import WCImage from '@/components/WCImage/WCImage.vue';
import WCLoginMenuV2 from '@/components/WCLoginMenuV2/WCLoginMenuV2.vue';
import WCUserGreetingV2 from '@/components/WCUserGreetingV2/WCUserGreetingV2.vue';
import WCActiveOrderTypeBadgeModern from '@/modules/orderTypes/components/WCActiveOrderTypeBadge/WCActiveOrderTypeBadgeModern.vue';
import { USER_GREETING_LAYOUT } from '@/constants/AppConstants';
import { capitalizeFirstLetter } from '@/utils/formatters';

const FAVICON_SRC = '/api/img/favicon?w=96&h=96';

export default {
  mixins: [HeaderScrollTransitionMixin, UserMixin, EmployeeMixin],
  components: {
    WCHamburgerMenu,
    WCImage,
    WCLoginMenuV2,
    WCUserGreetingV2,
    WCActiveOrderTypeBadgeModern,
  },
  data() {
    return {
      userGreetingLayout: USER_GREETING_LAYOUT,
      brandImageSrc: FAVICON_SRC,
      isUsingFaviconImage: true,
      hasTriedStoreImageFallback: false,
      headerResizeObserver: null,
    };
  },
  created() {
    this.updateHomeStore();
  },
  mounted() {
    this.updateHeaderHeightCssVar();
    this.observeHeaderHeight();
  },
  unmounted() {
    this.disconnectHeaderHeightObserver();
    document.documentElement.style.setProperty('--mobile-header-height', '0px');
  },
  computed: {
    getStoreImageSrc() {
      if (!this.$configuration.store.image) {
        return '';
      }
      return `${this.$configuration.cdn}/api/img/${this.$configuration.store.image}`;
    },
    getStoreName() {
      return this.$configuration.store.name;
    },
    isMultiStore() {
      return this.$configuration.multistore;
    },
    brandImageClass() {
      return this.isUsingFaviconImage
        ? 'logo wc-mobile-header-v2__brand-image wc-mobile-header-v2__brand-image--favicon'
        : 'logo wc-mobile-header-v2__brand-image wc-mobile-header-v2__brand-image--logo';
    },
    profileLabel() {
      if (this.isEmployee && this.user?.emn) {
        return capitalizeFirstLetter(this.user.emn.trim().split(/\s+/)[0]);
      }
      return capitalizeFirstLetter(this.name);
    },
  },
  methods: {
    isSideBarOpen() {
      return !!document.querySelector('.sidebar-panel');
    },
    /**
     * Overrides HeaderScrollTransitionMixin.handleHeaderOnScroll — called by scroll event.
     * Keeps header visible while mobile sidebar panel is open to prevent layout shift.
     */
    handleHeaderOnScroll() {
      if (this.isSideBarOpen()) {
        this.scrollClass = 'scrolled-up';
        return;
      }
      HeaderScrollTransitionMixin.methods.handleHeaderOnScroll.call(this);
    },
    updateHeaderHeightCssVar() {
      const totalOffset = this.$el?.getBoundingClientRect?.().bottom || this.$el?.offsetHeight || 0;
      document.documentElement.style.setProperty('--mobile-header-height', `${totalOffset}px`);
    },
    observeHeaderHeight() {
      if (typeof ResizeObserver !== 'function' || !this.$el) {
        return;
      }

      this.disconnectHeaderHeightObserver();
      const resizeObserver = new ResizeObserver(() => {
        this.updateHeaderHeightCssVar();
      });

      if (!resizeObserver || typeof resizeObserver.observe !== 'function') {
        return;
      }

      this.headerResizeObserver = markRaw(resizeObserver);
      this.headerResizeObserver.observe(this.$el);
    },
    disconnectHeaderHeightObserver() {
      if (this.headerResizeObserver && typeof this.headerResizeObserver.disconnect === 'function') {
        this.headerResizeObserver.disconnect();
      }
      this.headerResizeObserver = null;
    },
    toggleSideMenu() {
      SideMenuService.toggle();
    },
    fallBackToStoreLogo() {
      if (!this.hasTriedStoreImageFallback && this.getStoreImageSrc) {
        this.hasTriedStoreImageFallback = true;
        this.isUsingFaviconImage = false;
        this.brandImageSrc = this.getStoreImageSrc;
      }
    },
    updateHomeStore() {
      if (!this.isMultiStore) {
        this.setUserHomeStore(this.$configuration.store.id);
      }
    },
    goToAccountSettings() {
      this.$router.push({ name: 'Account Settings' }).catch(() => {});
    },
    goToSelectStore() {
      this.$router.push({ name: 'SelectStore' }).catch(() => {});
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.wc-mheader__wrapper {
  position: fixed;
  top: var(--assoc-banner-height, 0px);
  right: 0;
  left: 0;
  z-index: map-get($zindex, 'header');
  background-color: var(--white);
  box-shadow: 0 2px 5px 0 rgb(0 0 0 / 30%);
  padding-left: 0;
  padding-right: 0;

  &:has(+ .wc-mobile-header-padding .wc-mobile-subpage-header) {
    box-shadow: none;
    border-bottom: 1px solid rgb(0 0 0 / 30%);
  }
}

.wc-mheader__middle {
  min-height: convert-px-to-rem(48);

  .logo {
    max-width: 100%;
    max-height: 3rem;
  }

  .marginMenu {
    button {
      margin-left: convert-px-to-rem(18) !important;
    }
  }
}

.wc-mobile-header-v2__brand-image {
  display: block;
}

.wc-mobile-header-v2__brand-image--favicon {
  width: convert-px-to-rem(50);
  height: convert-px-to-rem(48);
  border-radius: convert-px-to-rem(999);
  object-fit: cover;
  border: 1px solid var(--gray-300, $gray-300);
  background-color: var(--white);
}

.wc-mobile-header-v2__brand-image--logo {
  width: auto;
  height: auto;
  border-radius: 0;
  border: 0;
  object-fit: contain;
  background-color: transparent;
}

.wc-mobile-header-v2__brand-container--employee {
  min-width: 0;
}

.wc-mobile-header-v2__profile-button {
  appearance: none;
  background: transparent;
  border: 0;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: rem(6px);
  padding: 0;
  margin-right: $margin-4;
  color: var(--body-color, $body-color);
  border-radius: rem(6px);
  min-width: 0;
  max-width: 100%;
}

.wc-mobile-header-v2__profile-button--customer-assoc {
  flex-direction: row;
  gap: rem(8px);
  align-items: center;
  background-color: var(--primary-lighter-9, var(--primary));
}

.wc-mobile-header-v2__profile-icon {
  font-size: $font-size-xl;
  color: var(--primary, $primary);
}

.wc-mobile-header-v2__profile-label {
  margin-top: convert-px-to-rem(2);
  font-size: $font-size-sm;
  line-height: 1.2;
  font-weight: 700;
  max-width: convert-px-to-rem(96);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.wc-mobile-header-v2__profile-sub-label {
  font-size: $font-size-xs;
  line-height: 1.2;
  max-width: convert-px-to-rem(96);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--body-color, $body-color);
}

.wc-mheader__wrapper :deep([data-testid='side-menu']) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.wc-mobile-header-v2__store-pill {
  width: 100%;
  margin-top: $margin-2;
  border: 1px solid var(--gray-400, $gray-400);
  border-radius: convert-px-to-rem(999px);
  background-color: var(--white);
  min-height: convert-px-to-rem(40);
  padding: $pad-2 $pad-3;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: $pad-2;
  text-align: left;
}

.wc-mobile-header-v2__store-icon,
.wc-mobile-header-v2__chevron {
  color: var(--primary, $primary);
}

.wc-mobile-header-v2__store-label {
  min-width: 0;
  font-size: $font-size-base;
  line-height: 1.2;
  color: var(--body-color, $body-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.header-options {
  gap: convert-px-to-rem(3);
}
</style>
